import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import Layout from '@/components/Layout';
import { COMPANIES, TALENTS, NEWS, ECOSYSTEM_STEPS, ROUTE_PATHS, companyPath } from '@/lib/index';

// ── Animated section wrapper ──────────────────────────────────────
function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function Home() {
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  const scrollToNext = () => {
    document.getElementById('ecosystem-intro')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Layout>
      {/* ──────────────────────────────────────────────────────────── */}
      {/* HERO — Fullscreen Cinematic                                   */}
      {/* ──────────────────────────────────────────────────────────── */}
      <section className="relative w-full h-screen min-h-[640px] overflow-hidden flex items-center justify-center">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1548208394-2360ca047eb3?w=1920&q=90"
            alt="ALV Pageant Circle Hero"
            className="w-full h-full object-cover object-center"
            onLoad={() => setHeroLoaded(true)}
          />
          {/* Multi-layer overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(8,8,8,0.55)] via-[rgba(8,8,8,0.35)] to-[rgba(8,8,8,0.75)]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[rgba(8,8,8,0.6)] via-transparent to-[rgba(8,8,8,0.3)]" />
        </div>

        {/* Gold glow orbs */}
        <div className="glow-orb w-[600px] h-[300px] bg-[rgba(201,168,76,0.04)] top-1/4 left-1/4" />
        <div className="glow-orb w-[400px] h-[200px] bg-[rgba(201,168,76,0.06)] bottom-1/3 right-1/4" />

        {/* Hero content */}
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={heroLoaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="eyebrow mb-6 md:mb-8">ALV Pageant Circle · Global Talent Ecosystem</p>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={heroLoaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="heading-display text-[clamp(3rem,8vw,7.5rem)] text-[#f5f5f5] mb-6"
          >
            Where Talent
            <br />
            <span className="shimmer-gold italic">Meets Opportunity</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={heroLoaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.65 }}
            className="text-[#f5f5f5]/60 text-base md:text-lg tracking-wide max-w-xl mx-auto mb-10 leading-relaxed"
          >
            A premium, vertically integrated ecosystem where exceptional individuals are discovered, developed, and elevated to the world stage.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={heroLoaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to={ROUTE_PATHS.ECOSYSTEM} className="btn-gold-outline">
              Explore Ecosystem
            </Link>
            <Link to={ROUTE_PATHS.TALENTS} className="btn-gold-fill">
              View Talents
            </Link>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={heroLoaded ? { opacity: 1 } : {}}
          transition={{ delay: 1.4, duration: 0.8 }}
          onClick={scrollToNext}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#c9a84c]/50 hover:text-[#c9a84c] transition-colors duration-300 cursor-pointer"
        >
          <span className="text-[9px] tracking-[0.25em] uppercase">Discover</span>
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          >
            <ChevronDown size={16} />
          </motion.div>
        </motion.button>
      </section>

      {/* ──────────────────────────────────────────────────────────── */}
      {/* ECOSYSTEM INTRO — Visual Stats Band                          */}
      {/* ──────────────────────────────────────────────────────────── */}
      <section id="ecosystem-intro" className="bg-[#0c0c0c] border-y border-[rgba(201,168,76,0.1)] py-14">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-0 md:divide-x divide-[rgba(201,168,76,0.1)]">
            {[
              { value: '5', label: 'Integrated Companies' },
              { value: '200+', label: 'Titleholders Produced' },
              { value: '30+', label: 'International Titles' },
              { value: '12+', label: 'Years of Excellence' },
            ].map((stat, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <div className="text-center md:px-8">
                  <p
                    className="heading-display text-[clamp(2.5rem,5vw,4rem)] text-gold mb-2"
                  >
                    {stat.value}
                  </p>
                  <p className="eyebrow opacity-60">{stat.label}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────── */}
      {/* ECOSYSTEM PIPELINE — Storytelling Flow                       */}
      {/* ──────────────────────────────────────────────────────────── */}
      <section className="py-28 bg-[#080808] relative overflow-hidden">
        <div className="glow-orb w-[800px] h-[400px] bg-[rgba(201,168,76,0.025)] top-0 left-1/2 -translate-x-1/2" />
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <FadeUp className="text-center mb-20">
            <p className="eyebrow mb-4">The ALV Ecosystem</p>
            <h2 className="heading-display text-[clamp(2.5rem,5vw,4.5rem)] text-[#f5f5f5] mb-6">
              A Complete Talent Pipeline
            </h2>
            <div className="divider-gold" />
            <p className="text-[#f5f5f5]/50 text-base max-w-2xl mx-auto mt-6 leading-relaxed">
              From first discovery to world-stage triumph — the ALV ecosystem provides every resource, platform, and opportunity exceptional talent needs to thrive.
            </p>
          </FadeUp>

          <div className="space-y-0">
            {ECOSYSTEM_STEPS.map((step, i) => (
              <FadeUp key={step.step} delay={i * 0.1}>
                <div className={`flex flex-col lg:flex-row ${i % 2 !== 0 ? 'lg:flex-row-reverse' : ''} gap-0 items-stretch`}>
                  {/* Text panel */}
                  <div className="flex-1 flex flex-col justify-center py-12 lg:py-16 px-8 lg:px-16 bg-[#090909] border border-[rgba(201,168,76,0.07)]">
                    <div className="flex items-start gap-5">
                      <span
                        className="heading-display text-[4rem] leading-none text-gold opacity-20 mt-1 flex-shrink-0"
                      >
                        {step.step}
                      </span>
                      <div>
                        <p className="eyebrow mb-3">{step.company}</p>
                        <h3 className="heading-display text-3xl text-[#f5f5f5] mb-4">{step.title}</h3>
                        <div className="divider-gold-left" />
                        <p className="text-[#f5f5f5]/55 text-sm leading-relaxed mt-4 max-w-md">
                          {step.description}
                        </p>
                        <Link
                          to={companyPath(step.companySlug)}
                          className="inline-flex items-center gap-2 mt-6 text-[11px] tracking-[0.2em] uppercase text-[#c9a84c] hover:text-[#f0c040] transition-colors duration-300"
                        >
                          Enter {step.company}
                          <ArrowRight size={12} />
                        </Link>
                      </div>
                    </div>
                  </div>
                  {/* Image panel */}
                  <div className="flex-1 min-h-[280px] lg:min-h-[360px] relative overflow-hidden">
                    <img
                      src={COMPANIES.find(c => c.slug === step.companySlug)?.portfolioItems[i % 4]?.image ?? COMPANIES[0].heroImage}
                      alt={step.title}
                      className="w-full h-full object-cover absolute inset-0 transition-transform duration-[1.2s] hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[rgba(8,8,8,0.4)] to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <span className="eyebrow opacity-80">{`Step ${step.step}`}</span>
                    </div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>

          <FadeUp className="text-center mt-16">
            <Link to={ROUTE_PATHS.ECOSYSTEM} className="btn-gold-outline">
              Explore Full Ecosystem
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────── */}
      {/* COMPANIES — Editorial Grid                                   */}
      {/* ──────────────────────────────────────────────────────────── */}
      <section className="py-28 bg-[#060606]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <FadeUp className="text-center mb-20">
            <p className="eyebrow mb-4">Our Companies</p>
            <h2 className="heading-display text-[clamp(2.5rem,5vw,4.5rem)] text-[#f5f5f5] mb-6">
              Five Pillars of Excellence
            </h2>
            <div className="divider-gold" />
          </FadeUp>

          {/* Primary featured — large + small grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            {/* Large featured card */}
            <FadeUp className="lg:col-span-2">
              <div className="card-editorial h-[520px] lg:h-[620px] group">
                <img
                  src={COMPANIES[0].heroImage}
                  alt={COMPANIES[0].name}
                  loading="lazy"
                />
                <div className="card-overlay" />
                <div className="absolute inset-0 flex flex-col justify-end p-10">
                  <div
                    className="w-12 h-12 flex items-center justify-center border text-[11px] font-bold tracking-widest mb-5 transition-colors duration-300"
                    style={{ borderColor: 'rgba(201,168,76,0.6)', color: '#c9a84c', fontFamily: 'var(--font-sans)' }}
                  >
                    {COMPANIES[0].logoInitials}
                  </div>
                  <p className="eyebrow mb-2 opacity-80">Main Brand</p>
                  <h3 className="heading-display text-4xl text-[#f5f5f5] mb-3">{COMPANIES[0].name}</h3>
                  <p className="text-[#f5f5f5]/60 text-sm leading-relaxed max-w-xs mb-6">{COMPANIES[0].description.slice(0, 100)}…</p>
                  <Link
                    to={companyPath(COMPANIES[0].slug)}
                    className="btn-gold-outline inline-block"
                    style={{ padding: '0.7rem 1.8rem' }}
                  >
                    Enter Site →
                  </Link>
                </div>
              </div>
            </FadeUp>

            {/* Right column — 2 stacked */}
            <div className="flex flex-col gap-4">
              {COMPANIES.slice(1, 3).map((company, i) => (
                <FadeUp key={company.id} delay={i * 0.12}>
                  <div className="card-editorial h-[298px] lg:h-[300px] group">
                    <img src={company.heroImage} alt={company.name} loading="lazy" />
                    <div className="card-overlay" />
                    <div className="absolute inset-0 flex flex-col justify-end p-7">
                      <p className="eyebrow mb-1 text-[9px]">{company.tagline}</p>
                      <h3 className="heading-display text-2xl text-[#f5f5f5] mb-4">{company.name}</h3>
                      <Link
                        to={companyPath(company.slug)}
                        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#c9a84c] hover:text-[#f0c040] transition-colors duration-200"
                      >
                        Enter Site <ArrowRight size={10} />
                      </Link>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>

          {/* Bottom row — 2 equal */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {COMPANIES.slice(3).map((company, i) => (
              <FadeUp key={company.id} delay={i * 0.12}>
                <div className="card-editorial h-[360px] group">
                  <img src={company.heroImage} alt={company.name} loading="lazy" />
                  <div className="card-overlay" />
                  <div className="absolute inset-0 flex flex-col justify-end p-8">
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="eyebrow mb-2 opacity-80">{company.tagline}</p>
                        <h3 className="heading-display text-3xl text-[#f5f5f5] mb-4">{company.name}</h3>
                        <Link
                          to={companyPath(company.slug)}
                          className="btn-gold-outline inline-block"
                          style={{ padding: '0.6rem 1.6rem' }}
                        >
                          Enter Site →
                        </Link>
                      </div>
                      <div
                        className="w-12 h-12 flex items-center justify-center border text-[11px] font-bold tracking-widest ml-4"
                        style={{ borderColor: 'rgba(201,168,76,0.5)', color: '#c9a84c', fontFamily: 'var(--font-sans)' }}
                      >
                        {company.logoInitials}
                      </div>
                    </div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────── */}
      {/* TALENTS — Editorial Scroll                                   */}
      {/* ──────────────────────────────────────────────────────────── */}
      <section className="py-28 bg-[#090909] relative overflow-hidden">
        <div className="glow-orb w-[600px] h-[300px] bg-[rgba(201,168,76,0.02)] top-1/2 right-0" />
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between mb-16 gap-6">
            <FadeUp>
              <p className="eyebrow mb-3">Our Talents</p>
              <h2 className="heading-display text-[clamp(2.5rem,5vw,4rem)] text-[#f5f5f5]">
                Faces of the Ecosystem
              </h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <Link to={ROUTE_PATHS.TALENTS} className="btn-gold-outline whitespace-nowrap">
                View All Talents
              </Link>
            </FadeUp>
          </div>

          {/* Asymmetric talent grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {TALENTS.slice(0, 8).map((talent, i) => (
              <FadeUp
                key={talent.id}
                delay={i * 0.07}
                className={i === 0 ? 'row-span-2 col-span-1' : ''}
              >
                <div className={`card-editorial group ${i === 0 ? 'h-[500px] md:h-[580px]' : 'h-[240px] md:h-[260px]'}`}>
                  <img src={talent.image} alt={talent.name} loading="lazy" />
                  <div className="card-overlay" />
                  <div className="absolute inset-0 flex flex-col justify-end p-4 md:p-5">
                    <p className="text-[9px] tracking-[0.2em] uppercase text-[#c9a84c]/80 mb-1">
                      {talent.category}
                    </p>
                    <h4 className="heading-display text-lg text-[#f5f5f5] leading-tight">{talent.name}</h4>
                    <p className="text-[10px] text-[#f5f5f5]/50 mt-1 line-clamp-1">{talent.title}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────── */}
      {/* NEWS / EVENTS — Minimal Cards                                */}
      {/* ──────────────────────────────────────────────────────────── */}
      <section className="py-28 bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between mb-16 gap-6">
            <FadeUp>
              <p className="eyebrow mb-3">Latest</p>
              <h2 className="heading-display text-[clamp(2.5rem,5vw,4rem)] text-[#f5f5f5]">
                News & Events
              </h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <Link to={ROUTE_PATHS.EVENTS} className="btn-gold-outline whitespace-nowrap">
                View All News
              </Link>
            </FadeUp>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {NEWS.slice(0, 3).map((item, i) => (
              <FadeUp key={item.id} delay={i * 0.12}>
                <article className="group cursor-pointer">
                  <div className="card-editorial h-56 mb-5">
                    <img src={item.image} alt={item.title} loading="lazy" />
                    <div className="card-overlay" />
                    <div className="absolute top-4 left-4">
                      <span className="eyebrow text-[9px] bg-[rgba(8,8,8,0.7)] px-2 py-1">
                        {item.category}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-[#c9a84c]/60 mb-2">{item.date}</p>
                    <h4
                      className="heading-display text-xl text-[#f5f5f5] leading-snug mb-3 group-hover:text-[#c9a84c] transition-colors duration-300"
                    >
                      {item.title}
                    </h4>
                    <p className="text-[#f5f5f5]/45 text-sm leading-relaxed line-clamp-2">{item.excerpt}</p>
                    <div className="mt-4 divider-gold-left opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </article>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────── */}
      {/* CTA SECTION                                                   */}
      {/* ──────────────────────────────────────────────────────────── */}
      <section className="relative py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1698303098477-fac2428d9dfe?w=1920&q=85"
            alt="Work with ALV"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-[rgba(8,8,8,0.8)]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[rgba(8,8,8,0.6)] via-transparent to-[rgba(8,8,8,0.6)]" />
        </div>

        <div className="glow-orb w-[500px] h-[250px] bg-[rgba(201,168,76,0.07)] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <FadeUp>
            <p className="eyebrow mb-6">Join the Ecosystem</p>
            <h2 className="heading-display text-[clamp(2.5rem,6vw,5.5rem)] text-[#f5f5f5] mb-6">
              Work with the
              <br />
              <span className="text-gold">ALV Ecosystem</span>
            </h2>
            <div className="divider-gold mb-8" />
            <p className="text-[#f5f5f5]/55 text-base md:text-lg leading-relaxed max-w-xl mx-auto mb-10">
              Whether you're an aspiring talent, a brand looking for partnerships, or an event organizer — the ALV ecosystem has the platform to elevate your vision.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to={ROUTE_PATHS.CONTACT} className="btn-gold-fill">
                Get in Touch
              </Link>
              <Link to={ROUTE_PATHS.ABOUT} className="btn-gold-outline">
                Learn About Us
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </Layout>
  );
}
